"use client";

import React, { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Sites from "src/constants/Sites";
import Entity from "@components/Entity";
import NEWSLETTER_DATA from "@components/NewsletterData/newsletter_data_2";
import NewsLetter2Card from "./NewsLetter2Card";
import Signup from "@components/Signup/Signup";
import { useAppContext } from "@components/Provider";
import {
  appendHttpsToUrl,
  doubleOptinParkingList,
  getUTMParams,
  readCookie,
  setCookie,
} from "helpers/Helper";
import browser from "src/utils/browser";
import Meta from "@components/Meta";
import styles from "./NewsLetters2.module.css";
import "./NewsLetters2.css";

const populateSEO = (title) => {
  let SEO_Data = {
    Title: `${title}`,
    Description:
      "At Open Road Media, we’re dedicated to providing you with books and content that are relevant to your interests. Join the newsletters below that best match your interests and receive high quality, curated content just for you.",
  };
  return SEO_Data;
};

const NewsLetters2 = (props) => {
  const nextRouter = useRouter();
  const { host } = props;
  const { data, siteId, router: ctxRouter, isMobile, li_did } = useAppContext();
  const { SiteSettings } = data;

  const [loading, setLoading] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const ebbOptInRef = useRef();
  const tluOptInRef = useRef();
  const portalistOptInRef = useRef();
  const murdermayhemOptInRef = useRef();
  const archiveOptInRef = useRef();
  const alovesotrueOptInRef = useRef();
  const youngreadersOptInRef = useRef();
  const theReaderOptInRef = useRef();

  const getRefByKey = (key) => {
    const pair = {
      ebbOptIn: ebbOptInRef,
      tluOptIn: tluOptInRef,
      portalistOptIn: portalistOptInRef,
      murdermayhemOptIn: murdermayhemOptInRef,
      archiveOptIn: archiveOptInRef,
      alovesotrueOptIn: alovesotrueOptInRef,
      youngreadersOptIn: youngreadersOptInRef,
      theReaderOptIn: theReaderOptInRef,
    };

    return pair[key];
  };

  const handleError = (error) => {
    if (error) {
      // E.L read errors from server
      const serverError = error.responseJSON;
      const errorMsg =
        serverError !== undefined
          ? serverError.error
          : "An Error occurred while subscribing to newsletter.";
      setDisplayError(true);
      setErrorMsg(errorMsg);
      setLoading(false);
    }
  };

  const handleSuccess = (response) => {
    const { res, token } = response;
    const { userAlreadyExist, email } = res?.MongoResponse?.info;
    if (userAlreadyExist) {
      if (email) {
        setCookie(
          "subscribed",
          email,
          process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS
        );
      }
      if (siteId === Sites.EARLYBIRDBOOKS) {
        setDisplayError(true);
        setErrorMsg(
          "You are already subscribed. Check your inbox for our daily emails."
        );
        setLoading(false);
        return;
      }
    }
    let newsletterOptIns = [];

    if (ebbOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("Early Bird Books");
    }

    if (tluOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("The Line Up");
    }

    if (portalistOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("The Portalist");
    }

    if (murdermayhemOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("Murder & Mayhem");
    }

    if (archiveOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("The Archive");
    }

    if (alovesotrueOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("A Love So True");
    }

    if (youngreadersOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("Young Readers");
    }

    if (theReaderOptInRef.current?.isOptedIn()) {
      newsletterOptIns.push("The Reader");
    }
    if (email) {
      setCookie("subscribed", email, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    }
    setCookie("ormtoken", token, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    setCookie("wasloggedin", true, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    setCookie("social_signup", true, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    setLoading(false);

    nextRouter.replace(
      siteId === Sites.EARLYBIRDBOOKS ? "/account/welcome" : "/thank-you"
    );
    trackSubmit();
  };

  const trackSubmit = () => {
    window.uetq = window.uetq || [];
    window.uetq.push({
      ec: "BingAd",
      ea: "NewsletterSignup",
      el: "PrimaryFunnel",
      ev: 1,
    });
    const gaDataLayer = {
      signupLocation: props.signUpLocation,
      event: "newsletter-component-submission",
    };
    window.dataLayer?.push(gaDataLayer);
  };

  const renderNewsletters = (newsletters, main_newsletter) => {
    newsletters.push(main_newsletter);
    const queryString = typeof window !== "undefined" && window.location.search;
    let utmParams = getUTMParams(queryString);
    utmParams = utmParams
      ? utmParams
      : readCookie("utm_params")
        ? JSON.parse(readCookie("utm_params"))
        : {};
    return newsletters.map((newsletterProps, key) => {
      let userOptionalParams = {
        vars: {
          signup_location: `${SiteSettings.site_name} old newsletter subscribe page`,
          slug: ctxRouter.asPath.substring(1),
          ...utmParams,
        },
      };

      if (browser.isClient()) {
        userOptionalParams.vars.referrer = document.referrer
          ? document.referrer
          : "";
        let primaryNewsletter = Sites.NEWSLETTERS.find((newsletter) => {
          return (
            window.location.hostname.indexOf(newsletter.NEWSLETTER_KEY) > -1
          );
        });

        if (primaryNewsletter) {
          primaryNewsletter = primaryNewsletter.NEWSLETTER_KEY;
        }
        userOptionalParams.site_id = newsletterProps.site_id;

        if (
          userOptionalParams.site_id === siteId &&
          Object.keys(utmParams).length === 0
        ) {
          userOptionalParams.vars.referral_source = "";
        } else {
          if (userOptionalParams.site_id !== siteId) {
            userOptionalParams.vars.referral_source =
              doubleOptinParkingList()[primaryNewsletter].utm_param;
          }
        }
      }
      const nlRef = getRefByKey(newsletterProps.refKey);
      return (
        <NewsLetter2Card
          parent_siteid={siteId}
          ref={nlRef}
          userOptionalParams={userOptionalParams}
          {...newsletterProps}
        />
      );
    });
  };

  const getMappedNewsletters = (usiteIndexArray) => {
    let finalData = [];
    for (let i = 0; i < usiteIndexArray.length; i++) {
      let element = usiteIndexArray[i];
      let data = NEWSLETTER_DATA.find((val) => {
        return val.usite_id == element;
      });
      finalData.push(data);
    }
    return finalData;
  };

  const renderNewsletterPromo = () => {
    const newsletters = NEWSLETTER_DATA;
    // this grabs the index of the json object that matches the current site ID
    const NEWSLETTER_INDEX = newsletters.findIndex((i) => i.site_id === siteId);

    // this is the newsletter obj of current site;
    const nl_obj = newsletters[NEWSLETTER_INDEX];

    // this is the array of newsletter objs based on usite_order
    const orderedNL = getMappedNewsletters(nl_obj.usite_order);
    const BG = {
      backgroundImage: `url("${nl_obj.imageURL}")`,
      backgroundSize: `cover`,
    };
    const pageUrl = appendHttpsToUrl(`${host}${ctxRouter.asPath}`);
    const siteName = SiteSettings.site_name;

    return (
      <>
        <Meta
          url={pageUrl}
          static={populateSEO(`Newsletters | ${siteName}`)}
          pageType={Entity.STATIC}
          siteName={siteName}
          domain={host}
          Homepage_SEO_Image={`${nl_obj.imageURL}`}
          isNewAppRouter={true}
        />
        <section className={[styles["newsletter-promo"]].join(" ")}>
          {/* Header */}
          <div className={styles["banner-main"]}>
            <div
              className={[
                styles["banner-sub"],
                styles["newsletter-header"],
              ].join(" ")}
            >
              <div className={[styles["left"]].join(" ")}>
                <div className={[styles["content"]].join(" ")}>
                  <h2
                    style={{ color: `${nl_obj.headerColor}` }}
                    dangerouslySetInnerHTML={{ __html: nl_obj.subheader }}
                  ></h2>
                  <p
                    style={{ color: `${nl_obj.subColor}` }}
                    dangerouslySetInnerHTML={{ __html: nl_obj.content }}
                  ></p>
                </div>
                <div className={styles["banner-img"]}>
                  <img
                    src={nl_obj.imageURL}
                    alt="Illustration"
                    className={styles["illustration"]}
                  />
                </div>
              </div>

              <div className={styles["right"]}>
                <Signup
                  siteId={siteId}
                  li_did={li_did}
                  orderedRefs={{
                    ebbOptIn: ebbOptInRef,
                    tluOptIn: tluOptInRef,
                    portalistOptIn: portalistOptInRef,
                    murdermayhemOptIn: murdermayhemOptInRef,
                    archiveOptIn: archiveOptInRef,
                    alovesotrueOptIn: alovesotrueOptInRef,
                    youngreadersOptIn: youngreadersOptInRef,
                    theReaderOptIn: theReaderOptInRef,
                  }}
                  errorMessage={errorMsg}
                  showError={displayError}
                  handleSuccess={handleSuccess}
                  handleError={handleError}
                />
              </div>
            </div>
          </div>
          {/* Sites Grid */}
          <div className={styles["newsletter-section"]}>
            <div
              className={styles["container-fluid"]}
              style={{ color: `${nl_obj.bridgeColor}` }}
            >
              <h3>Even more great content awaits!</h3>
              <p>
                Sign up for as many newsletters as you like to discover a new
                place to explore your passions
              </p>
              <div>
                <div className={styles["newsletter-grid"]}>
                  {renderNewsletters(orderedNL, nl_obj)}
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };
  return <>{renderNewsletterPromo()}</>;
};

export default NewsLetters2;
